import { useState, useEffect } from 'react'
import { useTuningStore } from '../store/tuningStore'
import { getNoteName, getRelativeNoteIndex, calculateFrequencyFromTonic, getMidiNoteInOctave, NOTE_NAMES } from '../utils/noteUtils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { X, Trash2 } from 'lucide-react'

export const TuningPanel = () => {
  const {
    selectedNote,
    selectedExtraNoteId,
    extraNotes,
    noteOffsets,
    setNoteOffset,
    resetNoteOffset,
    selectNote,
    tonicNote,
    tonicFrequency,
    referencePitch,
    setExtraNoteCents,
    resetExtraNoteCents,
    removeExtraNote,
    selectExtraNote,
  } = useTuningStore()
  const [localValue, setLocalValue] = useState('0')

  const extraNote = selectedExtraNoteId ? extraNotes.find((n) => n.id === selectedExtraNoteId) ?? null : null
  const offset = selectedNote !== null ? noteOffsets[selectedNote] : extraNote ? extraNote.cents : null

  useEffect(() => {
    if (offset !== null) {
      setLocalValue(offset.toFixed(1))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNote, selectedExtraNoteId, offset])

  if (offset === null) {
    return null
  }

  const applyValue = (val: number) => {
    const clamped = Math.max(-300, Math.min(300, val))
    if (selectedNote !== null) {
      setNoteOffset(selectedNote, clamped)
    } else if (extraNote) {
      setExtraNoteCents(extraNote.id, clamped)
    }
  }

  const handleSliderChange = (value: number[]) => {
    const val = value[0]
    applyValue(val)
    setLocalValue(val.toFixed(1))
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalValue(value)
    const numValue = parseFloat(value)
    if (!isNaN(numValue)) {
      applyValue(numValue)
    }
  }

  const handleReset = () => {
    if (selectedNote !== null) {
      resetNoteOffset(selectedNote)
    } else if (extraNote) {
      resetExtraNoteCents(extraNote.id)
    }
    setLocalValue('0')
  }

  const handleClose = () => {
    selectNote(null)
    selectExtraNote(null)
  }

  const handleDeleteExtraNote = () => {
    if (extraNote) {
      removeExtraNote(extraNote.id)
    }
  }

  const offsetColor = offset > 0 ? 'text-blue-600' : offset < 0 ? 'text-rose-600' : 'text-foreground'

  const displayName = selectedNote !== null ? getNoteName(selectedNote) : extraNote ? `${NOTE_NAMES[extraNote.noteIndex]}${extraNote.octave}` : ''

  // Calculate frequency in Hz for the selected note based on tonic
  const frequency =
    selectedNote !== null
      ? tonicFrequency * Math.pow(2, (getRelativeNoteIndex(selectedNote, tonicNote) + offset / 100) / 12)
      : extraNote
        ? calculateFrequencyFromTonic(
            getMidiNoteInOctave(extraNote.octave, extraNote.noteIndex),
            extraNote.cents,
            tonicNote,
            tonicFrequency,
            referencePitch
          )
        : 0

  return (
    <Card className="animate-fade-in">
      <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <span className="text-lg font-bold text-primary-foreground">{displayName}</span>
                </div>
                <div>
                  <CardTitle>Réglage: {displayName}</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {extraNote ? 'Note supplémentaire · offset indépendant' : 'Offset en cents'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {extraNote && (
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Supprimer cette note"
                    onClick={handleDeleteExtraNote}
                  >
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Offset</Label>
            <div className="text-right">
              <div className={`text-xl font-bold ${offsetColor}`}>
                {offset > 0 ? '+' : ''}{offset.toFixed(1)}¢
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {frequency.toFixed(2)} Hz
              </div>
            </div>
          </div>
          <Slider
            value={[offset]}
            onValueChange={handleSliderChange}
            min={-300}
            max={300}
            step={0.1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span className="text-rose-600">-300¢</span>
            <span>0¢</span>
            <span className="text-blue-600">+300¢</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Valeur précise</Label>
            <Input
              type="number"
              min="-300"
              max="300"
              step="0.1"
              value={localValue}
              onChange={handleInputChange}
              className="font-mono"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={handleReset}
              variant="secondary"
              className="w-full"
            >
              ↺ Reset
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
